import "@shoelace-style/shoelace/dist/components/switch/switch.js";
import "@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js";
import "@shoelace-style/shoelace/dist/components/carousel/carousel.js";
import "@shoelace-style/shoelace/dist/components/carousel-item/carousel-item.js";

import { v4 as uuidv4 } from "uuid";
import { html, PropertyValueMap } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { BaseElement } from "../BaseElement";
import {
    exifLocation,
    getMediaTypeFromMimeType,
    MAX_LORES_IMAGE_WIDTH,
    MAX_HIRES_IMAGE_WIDTH,
    MAX_VIDEO_SIZE_MB,
    MediaType,
    uploadMedia,
    exifDateTimeOriginal,
    processImageForUpload,
    getMimeTypeFromExtension,
} from "../../Media";
import {
    distanceInBetweenLocationsMeters,
    getClosestPlaces,
    ILocation,
} from "../../models/ILocation";
import { IMedia, IPlace } from "../../models/Models";
import { makeCustomEvent } from "../../Utils";
import { getModuleLog } from "../../Logger";
import { styles } from "./MediaImport.style";
import { litIf } from "../LitUtils";
import cloneDeep from "lodash-es/cloneDeep.js";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { MediaFields } from "../Media/Media.form";

import "./Media";
import "../form/FormControl";
import "../FixedPanel/FixedPanel";
import { Form } from "../form/Form";
import { showYouTubeEmbedDialog } from "../YouTubeEmbedControl/YouTubeEmbedControl";
import { showSketchFabEmbedDialog } from "../SketchFabEmbedControl/SketchFabEmbedControl";
import { LocalStorageProperty } from "../../LocalStorageProperty";

type PlaceDist = {
    place: IPlace;
    dist: number;
};

type ImportFile = {
    file?: File; // this maybe undefined if it is a youtube video
    url: string;
    assigned: boolean;
    location?: ILocation;
    closestPlace?: PlaceDist;
    places: PlaceDist[];
    media: IMedia;
    mediaType: MediaType;
    error?: string;
    dateTaken?: string;
};

@customElement("ds-media-import")
export class MediaImport extends BaseElement {
    static override styles = [styles];

    @property({ type: Object }) place?: IPlace;
    @property({ type: Boolean }) noHeader = false;

    @state() protected _files?: ImportFile[];
    @state() protected _currentFile?: ImportFile;
    @state() protected _currentIndex?: number;
    @state() protected _importingFile = false;
    @state() protected _uploadProgress = 0;
    @state() protected _lastSelectedPlace?: IPlace;
    @state() protected _currentHasPlace?: boolean;
    @state() protected _highRes = false;
    @state() protected _addWatermark = false;
    @state() protected _ignoreImageDate = false;
    @state() protected _currentViewMedia?: IMedia;

    private _watermark = new LocalStorageProperty(this._kernel.events, "media-import-watermark", 1);

    private _placesTable = this._kernel.dataStore.getSource<PlaceTable>("places");

    private _log = getModuleLog("media-import");
    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
    }

    protected override firstUpdated(
        _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        super.firstUpdated(_changedProperties);
    }

    public async placedSelected(id: string) {
        if (this._currentFile) {
            const place = await this._placesTable.read(id);
            if (place) {
                this._lastSelectedPlace = cloneDeep(place);
                this._currentHasPlace = true;
                this._currentFile.closestPlace = {
                    place: this._lastSelectedPlace,
                    dist: this._currentFile.location
                        ? distanceInBetweenLocationsMeters(
                              place.data.location,
                              this._currentFile.location
                          )
                        : 0,
                };
            }
            this.requestUpdate();
        }
    }

    private _attributeToCurrentUser(file: ImportFile | undefined) {
        const user = this._kernel.user;
        const form = this.shadowRoot?.getElementById("media-form") as Form;
        if (user && file && form) {
            form.updateProperties({
                attribution: undefined,
                contributorId: user.id,
            });
        }
    }

    private _setClosestPlace(place: IPlace) {
        if (!this._currentFile) {
            return;
        }

        this._currentFile.closestPlace = {
            place,
            dist: this._currentFile.location
                ? distanceInBetweenLocationsMeters(place.data.location, this._currentFile.location)
                : 0,
        };
        this._currentHasPlace = true;
    }

    private _startSelectPlace() {
        this.dispatchEvent(makeCustomEvent("select-place"));
    }

    private async _importExternalLink(mimeType: string, url: string) {
        const closestPlaces = this.place ? [{ place: this.place, dist: 0 }] : [];
        this._files = [
            {
                file: undefined,
                url: url,
                assigned: false,
                location: undefined,
                places: closestPlaces,
                closestPlace: closestPlaces.length ? closestPlaces[0] : undefined,
                mediaType: MediaType.YouTube,
                media: {
                    id: uuidv4(),
                    mimeType: mimeType,
                    file: {
                        bucket: "",
                        region: "",
                        key: url,
                    },
                    name: "",
                    description: "",
                    attribution: undefined,
                    contributorId: undefined,
                },
            },
        ];

        this._setCurrentIndex(0);

        this._raiseGoToLocationEvent();
        this._raisePhotoLocationEvent();
    }

    private async _importMedia(inputFiles: FileList | File[]) {
        // find list of closest places
        const files: ImportFile[] = [];
        for (const file of inputFiles) {
            let location = undefined;
            let fileType = file.type;
            if (!fileType || fileType.length === 0) {
                fileType = getMimeTypeFromExtension(file.name) ?? "";
            }
            if (fileType === "") {
                this._showError("Unknown file type");
                return;
            }
            const mediaType = getMediaTypeFromMimeType(fileType);
            if (mediaType === MediaType.Image) {
                const exifLoc = await exifLocation(file);
                if (exifLoc) {
                    location = exifLoc
                        ? {
                              lat: exifLoc.latitude,
                              lon: exifLoc.longitude,
                          }
                        : undefined;
                }
            }
            let closestPlaces: PlaceDist[] = [];
            if (this.place) {
                closestPlaces = [{ place: this.place, dist: 0 }];
            } else if (location) {
                closestPlaces = getClosestPlaces(await this._placesTable.all(), location, 8, 250);
            }
            const url = URL.createObjectURL(file);

            files.push({
                file,
                url: url,
                assigned: false,
                location,
                places: closestPlaces,
                closestPlace: closestPlaces.length ? closestPlaces[0] : undefined,
                mediaType,
                media: {
                    id: uuidv4(),
                    mimeType: file.type,
                    file: {
                        bucket: "",
                        region: "",
                        key: url,
                    },
                    name: file.name,
                    description: "",
                    attribution: undefined,
                    contributorId: undefined,
                    dateCreated: await exifDateTimeOriginal(file),
                    dateAdded: new Date().toISOString(),
                },
            });
        }
        this._files = files;

        if (this._files.length > 0) {
            this._setCurrentIndex(0);
        }

        this._raiseGoToLocationEvent();
        this._raisePhotoLocationEvent();

        //TODO: add matching revokeObjectURL() calls.
    }

    private _showError(msg: string) {
        this._log.error(`Media import error : ${msg}`);
    }

    private _discardCurrentFile() {
        if (this._currentIndex !== undefined && this._currentFile && this._files) {
            this._files = this._files.filter((x) => x !== this._currentFile);
            if (this._files.length === 0) {
                this._currentFile = undefined;
                this._currentIndex = undefined;
                this._currentViewMedia = undefined;
                this._currentHasPlace = false;
            } else {
                if (this._currentIndex === this._files.length) {
                    this._currentIndex = 0;
                }
                this._currentFile = this._files[this._currentIndex];
                this._currentHasPlace = !!this._currentFile.closestPlace;
                this._updateCurrentViewMedia();
            }
        }
    }

    private async _importCurrentFile() {
        if (!this._currentFile?.closestPlace) {
            return;
        }

        this._importingFile = true;
        try {
            const uploadData = {
                place: this._currentFile.closestPlace.place.id,
                date: Date.now(),
            };

            let media: IMedia | undefined;

            if (this._currentFile.file) {
                const result = await uploadMedia(
                    this._currentFile?.file,
                    {
                        highRes: this._highRes,
                        watermark: this._addWatermark ? this._watermark.get() : undefined,
                    },
                    uploadData
                );
                if (result.succeeded) {
                    media = result.media;
                } else if (result.error) {
                    this._currentFile.error = result.error;
                    this._showError(result.error);
                }
            } else {
                // youtube video
                media = this._currentFile.media;
            }
            if (media) {
                // update the place
                const place = this._currentFile.closestPlace.place;

                media.description = this._currentFile.media.description;
                media.attribution = this._currentFile.media.attribution;
                media.attributionLink = this._currentFile.media.attributionLink;
                media.contentType = this._currentFile.media.contentType;
                media.contributorId = this._currentFile.media.contributorId;

                if (this._ignoreImageDate) {
                    media.dateCreated = undefined;
                }

                await this._placesTable.createMediaItem(
                    place.id,
                    media,
                    place.groupAccess as string[]
                );

                if (this._currentFile.closestPlace) {
                    this._lastSelectedPlace = this._currentFile.closestPlace.place;
                }
                this._discardCurrentFile();
            }
        } catch (error: any) {
            this._currentFile.error = error;
            this._showError(error);
        } finally {
            this._importingFile = false;
            this._uploadProgress = 0;
        }
    }

    private _reset() {
        this._currentFile = undefined;
        this._currentHasPlace = false;
        this._currentViewMedia = undefined;
        this._files = undefined;
    }

    private _setCurrentIndex(n: number) {
        if (this._files && n < this._files.length) {
            this._currentIndex = n;
            this._currentFile = this._files[n];
            this._currentHasPlace = !!this._currentFile.closestPlace;
            this._updateCurrentViewMedia();
        }
    }

    private _next() {
        if (this._currentIndex !== undefined && this._files) {
            let n = this._currentIndex + 1;
            if (n === this._files?.length) {
                n = 0;
            }
            this._setCurrentIndex(n);
        }
        this._raisePhotoLocationEvent();
    }

    private _prev() {
        if (this._currentIndex !== undefined && this._files) {
            let n = this._currentIndex - 1;
            if (n < 0) {
                n = this._files?.length - 1;
            }
            this._setCurrentIndex(n);
        }
        this._raisePhotoLocationEvent();
    }

    private _raiseLocationEvent(eventName: string, location: ILocation) {
        if (location) {
            this.dispatchEvent(
                makeCustomEvent(eventName, {
                    location: location,
                    file: this._currentFile,
                })
            );
        }
    }

    private _raisePhotoLocationEvent() {
        if (this._currentFile?.location) {
            this._raiseLocationEvent("photo-location-changed", this._currentFile.location);
        }
        this._raiseGoToLocationEvent();
    }

    private _raiseGoToLocationEvent() {
        let location = this._currentFile?.location;
        if (this._currentFile?.closestPlace) {
            location = this._currentFile.closestPlace.place.data.location;
        }
        if (location) {
            this._raiseLocationEvent("go-to-location", location);
        }
    }

    private _dropHandler(e: any) {
        // Prevent default behavior (Prevent file from being opened)
        e.preventDefault();

        const files: File[] = [];

        if (e.dataTransfer.items) {
            // Use DataTransferItemList interface to access the file(s)
            [...e.dataTransfer.items]
                .filter((x) => x.kind === "file")
                .forEach((x) => files.push(x.getAsFile()));
        } else {
            // Use DataTransfer interface to access the file(s)
            [...e.dataTransfer.files].forEach((file) => files.push(file));
        }

        this._importMedia(files);
    }

    private _dragOverHandler(e: any) {
        // Prevent default behavior (Prevent file from being opened)
        e.preventDefault();
    }

    private async _updateCurrentViewMedia() {
        if (!this._currentFile || !this._currentFile.file) {
            this._currentViewMedia = undefined;
            return;
        }
        if (this._currentFile.mediaType !== MediaType.Image) {
            this._currentViewMedia = this._currentFile.media;
            return;
        }

        const newMedia = cloneDeep(this._currentFile.media);

        const processedImage = await processImageForUpload(this._currentFile.file, {
            highRes: this._highRes,
            watermark: this._addWatermark ? this._watermark.get() : undefined,
        });
        newMedia.file.key = URL.createObjectURL(processedImage);
        this._currentViewMedia = newMedia;
    }

    private _renderDragDropTarget() {
        return html`<div class="drag-drop-target flex-column">
            <p style="text-align:justify">
                ${`Upload image, video, pdf, geojson and kml files or a link to a youtube video.`}
                <br /><br />
                ${`Images by default will be constrained to a maximum size of ${MAX_LORES_IMAGE_WIDTH}px 
                        unless 'High resolution' is selected in which case it will be constrained to maximum size 
                        of ${MAX_HIRES_IMAGE_WIDTH}px. This will use location metadata if available to suggest the closest place. All location and other metadata will be removed.`}

                <br /><br />
                ${`Video files have a maximum size of ${MAX_VIDEO_SIZE_MB}MB.`}
            </p>

            <sl-button
                @click=${() => this.shadowRoot?.getElementById("media-import-input")?.click()}
                >Browse for files</sl-button
            >
            <p style="text-align:justify">-&nbsp;OR&nbsp;-</p>
            <sl-button
                @click=${() =>
                    showYouTubeEmbedDialog({}, (url: string) =>
                        this._importExternalLink("application/youtube", url)
                    )}
                >Add YouTube link</sl-button
            >
            <p style="text-align:justify">-&nbsp;OR&nbsp;-</p>
            <sl-button
                @click=${() =>
                    showSketchFabEmbedDialog({}, (url: string) =>
                        this._importExternalLink("application/sketchfab", url)
                    )}
                >Add SketchFab link</sl-button
            >
            <p style="text-align:justify">-&nbsp;OR&nbsp;-</p>
            <div
                class="content"
                @drop=${(e: any) => this._dropHandler(e)}
                @dragover=${(e: any) => this._dragOverHandler(e)}
            >
                <p>Drag and drop files here to import</p>
            </div>
        </div>`;
    }

    private _renderContainer() {
        return html`<ds-fixed-panel class="import-view">
            ${litIf(
                !this.noHeader,
                () =>
                    html`<header slot="header">
                        <h3>Import Media</h3>
                    </header>`
            )}
            <article slot="body">
                ${this._currentFile ? this._renderImportViewBody() : this._renderDragDropTarget()}
            </article>
            <footer slot="footer">
                <div class="button-bar flex-row">
                    ${litIf(
                        !this._importingFile && !!this._currentFile,
                        () => html`
                            <sl-button
                                size="medium"
                                @click=${() => {
                                    this._reset();
                                }}
                                >Clear all</sl-button
                            >
                            <sl-button size="medium" @click=${() => this._discardCurrentFile()}
                                >Discard</sl-button
                            ><sl-button
                                size="medium"
                                @click=${() => this._importCurrentFile()}
                                ?disabled=${!this._currentHasPlace}
                                >Import</sl-button
                            >
                        `
                    )}
                    <sl-button size="medium" @click=${() => this._raiseEvent("ds-close")}
                        >Close</sl-button
                    >
                </div>
            </footer>
        </ds-fixed-panel>`;
    }

    private _renderImportViewBody() {
        return html`${this._currentFile
            ? html` <div class="container">
                  <div class="footer flex-column">
                      <div class="button-bar">
                          <sl-icon-button
                              name="arrow-left-square"
                              @click=${() => this._prev()}
                          ></sl-icon-button>
                          <span style="flex-grow: 1;text-align: center; font-size:1.3em">
                              ${this._currentIndex !== undefined
                                  ? `${this._currentIndex + 1} / ${this._files?.length}`
                                  : ``}
                          </span>
                          <sl-icon-button
                              name="arrow-right-square"
                              @click=${() => this._next()}
                          ></sl-icon-button>
                      </div>
                      ${litIf(
                          this._importingFile,
                          () =>
                              html`<div style="display:flex; flex-direction:column">
                                  <h4>
                                      Uploading
                                      <span style="font-style:italic"
                                          >${this._currentFile!.file?.name}</span
                                      >
                                  </h4>
                                  <sl-progress-bar
                                      .value=${this._uploadProgress * 100}
                                  ></sl-progress-bar>
                              </div>`
                      )}
                      ${litIf(
                          !this.place && !this._importingFile,
                          () => html`
                              <sl-button size="medium" @click=${() => this._startSelectPlace()}
                                  >Select place</sl-button
                              >
                              <sl-button
                                  size="medium"
                                  style=${`display: ${this._lastSelectedPlace ? "block" : "none"}`}
                                  @click=${() => {
                                      if (this._lastSelectedPlace) {
                                          this._setClosestPlace(this._lastSelectedPlace);
                                          this.requestUpdate();
                                      }
                                  }}
                                  >Use last place</sl-button
                              >
                          `
                      )}
                  </div>
                  <ds-media .media=${this._currentViewMedia} hideCloseButton></ds-media>
                  <sl-button
                      pill
                      variant="success"
                      @click=${() => this._attributeToCurrentUser(this._currentFile)}
                      >Attribute to me</sl-button
                  >
                  ${litIf(
                      this._currentFile.mediaType === MediaType.Image,
                      () =>
                          html` <sl-switch
                                  ?checked=${this._highRes}
                                  @sl-change=${(e: any) => {
                                      this._highRes = e.target.checked;
                                      this._updateCurrentViewMedia();
                                  }}
                                  >High resolution</sl-switch
                              ><sl-switch
                                  ?checked=${this._addWatermark}
                                  @sl-change=${(e: any) => {
                                      this._addWatermark = e.target.checked;
                                      this._updateCurrentViewMedia();
                                  }}
                                  >Add watermark</sl-switch
                              >
                              <sl-switch
                                  ?checked=${this._ignoreImageDate}
                                  @sl-change=${(e: any) => {
                                      this._ignoreImageDate = e.target.checked;
                                      this._updateCurrentViewMedia();
                                  }}
                                  >Ignore image date metadata</sl-switch
                              >
                              <sl-input
                                  pill
                                  label="Add watermark to image"
                                  placeholder="Bob McCave 2024"
                                  .value=${this._watermark.get() ?? ""}
                                  @sl-change=${(e: any) => {
                                      this._watermark.set(e.target.value);
                                      this._updateCurrentViewMedia();
                                  }}
                              ></sl-input>`
                  )}

                  <ds-form
                      id="media-form"
                      .object=${this._currentFile?.media}
                      .fields=${MediaFields}
                      @ds-form-changed=${(e: CustomEvent) => {
                          if (this._currentFile) {
                              this._currentFile.media = e.detail.object;
                              this._updateCurrentViewMedia();
                              this.requestUpdate();
                          }
                      }}
                  ></ds-form>
                  <div style="display:flex; flex-direction:column; width:100%; gap:0.2em;">
                      ${litIf(
                          !!this._currentFile?.error,
                          () => html`<span class="error">${this._currentFile!.error}</span>`
                      )}
                      <span
                          >Place :
                          ${this._currentFile?.closestPlace
                              ? this._currentFile?.closestPlace?.place.data.name
                              : "unknown"}</span
                      >
                      ${this._currentFile?.closestPlace
                          ? html`<span
                                >Distance: ${Math.floor(this._currentFile?.closestPlace?.dist)}
                                meters</span
                            >`
                          : html``}
                  </div>
              </div>`
            : html``} `;
    }

    override render() {
        return html` ${this._renderContainer()}
            <input
                id="media-import-input"
                type="file"
                multiple
                @change=${(event: any) => this._importMedia(event.target?.files)}
                style="visibility:collapse; display:none"
                accept="image/*,video/mp4,application/vnd.google-earth.kml+xml,.kml,application/pdf,application/json"
            />`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-media-import": MediaImport;
    }
}
